import { useState, useEffect, useRef, useCallback } from 'react';
import { Truck, Search, X, Check } from 'lucide-react';
import shipmentCrossingService from '../../services/shipmentCrossingService.js';

/**
 * Searchable single-vehicle selector for crossing.
 * Calls GET /shipment-crossings/vehicles?search= and lets the user pick one.
 */
const CrossingVehicleSelect = ({ value, onChange, disabled = false }) => {
  const [search, setSearch] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const fetchVehicles = useCallback(async (term) => {
    try {
      setLoading(true);
      const res = await shipmentCrossingService.listVehicles({ search: term });
      setVehicles(res?.data || []);
    } catch (err) {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => fetchVehicles(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search, open, fetchVehicles]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleSelect = (vehicle) => {
    onChange?.(vehicle);
    setOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange?.(null);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Select Vehicle <span className="text-red-500">*</span>
      </label>

      {value ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
          <span className="flex items-center gap-2 text-sm font-medium text-gray-800 min-w-0">
            <Truck className="w-4 h-4 text-orange-500 shrink-0" />
            <span className="truncate">
              {value.vehicle_number}
              {value.vehicle_name ? ` — ${value.vehicle_name}` : ''}
            </span>
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded text-gray-500 hover:bg-gray-200"
              aria-label="Clear selected vehicle"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            placeholder="Search vehicle number or name..."
            className="login-input w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
          />
        </div>
      )}

      {open && !value && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {loading ? (
            <div className="p-3 text-sm text-gray-500">Loading vehicles...</div>
          ) : vehicles.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">No active vehicles found</div>
          ) : (
            vehicles.map((vehicle) => (
              <button
                key={vehicle.id}
                type="button"
                onClick={() => handleSelect(vehicle)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-orange-50"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Truck className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="min-w-0">
                    <span className="font-medium text-gray-800">{vehicle.vehicle_number}</span>
                    {vehicle.vehicle_name ? (
                      <span className="text-gray-500"> — {vehicle.vehicle_name}</span>
                    ) : null}
                    {vehicle.drop_point_summary ? (
                      <span className="block text-xs text-gray-400 truncate">
                        Route: {vehicle.pickup_point_summary || '—'} → {vehicle.drop_point_summary}
                      </span>
                    ) : null}
                  </span>
                </span>
                <Check className="w-4 h-4 text-transparent" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CrossingVehicleSelect;
